// Dashboard as-needed medication quick-log (MEDICATION_QUICK_LOG). Lets the user
// mark a dose taken by picking from their saved medications, adjusting the dose/
// quantity and time, and confirming — without going through chat (no Bedrock).
//
// Flow: pick → adjust → review → done. Multi-select and regimen-group taps skip
// adjust and land on Review & adjust (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md).
//  - pick:   searchable list of saved meds (as-needed first, then scheduled).
//  - adjust: editable dose (or a +/- quantity stepper for countable units) + Now/Custom time.
//  - review: server-prepared read-back + safety warnings (allergy/interaction/overdose/
//            food/side effect). One medication is read-only. Several get a stepper
//            each; changes re-check on the server (debounced) before Save unlocks.
//  - done:   success + "Log another".
// The prepare call stores a pending batch; Save confirms it through confirmEvents,
// the same gated path the proactive suggestion card uses.

import { useEffect, useMemo, useRef, useState } from "react";
import type { MedUnit } from "@medassist/shared";

function todayStr() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}
/** Earliest selectable custom date — the server's sanity floor (not the 7-day backfill window). */
function minCustomDateStr() {
  const d = new Date();
  d.setDate(d.getDate() - CUSTOM_TAKEN_AT_MAX_DAYS_BACK + 1); // +1 day keeps clear of the server's exact cutoff
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function nowTimeStr() {
  const n = new Date();
  return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
}
import {
  CUSTOM_TAKEN_AT_MAX_DAYS_BACK,
  formatMedStrength,
  hasPillStepper,
  isAdjusted,
  pillCountForDose,
  doseForPillCount,
  formatPillCount,
  pillStep,
  roundPillCount,
  usualDose,
  type DoseControlItem,
  type MedIngredient,
} from "@medassist/shared";
import {
  quickLogMedication,
  quickLogMedicationBatch,
  confirmEvents,
  ApiError,
  type ProactiveSuggestion,
  type SavedEventRef,
} from "../lib/api.js";
import { matchesMedicationSearch } from "../lib/conditionSynonyms.js";
import { PillIcon, InfoIcon } from "./icons.js";
import { ModalShell } from "./ModalShell.js";
import { medLabelWithoutTime } from "./SuggestionCard.js";
import { MedicationInfoModal } from "./MedicationInfoModal.js";
import {
  useMedChoices,
  MedSection,
  RegimenGroupRow,
  type MedChoice,
  type RegimenGroupChoice,
} from "./medPicker.js";
import { ClearableInput, ClearableTextarea } from "./ClearableInput.js";
import { DoseAdjustRow, DoseCheckSpinner } from "./DoseAdjustRow.js";
import { createLatestRunner } from "../lib/latestOnly.js";

type ReviewItem = { regimenId: string; lineId: string };

function withDoses(
  items: ReviewItem[],
  edits: Record<string, number>
): { regimenId: string; lineId: string; dose?: number }[] {
  return items.map((it) => {
    const dose = edits[`${it.regimenId}:${it.lineId}`];
    return dose !== undefined ? { ...it, dose } : it;
  });
}

function eventDose(payload: Record<string, unknown>): number {
  const d = payload.dose;
  return typeof d === "number" && d > 0 ? d : 0;
}

function choiceToItem(c: MedChoice): DoseControlItem & { name: string; strength: string } {
  const ingredients: MedIngredient[] | undefined = c.hasIngredients
    ? [{ name: c.name, dose: c.dose && c.dose > 0 ? c.dose : 1, unit: c.unit ?? "mg" }]
    : undefined;
  return {
    name: c.name,
    strength: c.strength,
    dose: c.dose,
    unit: c.unit,
    pillStrength: c.pillStrength,
    pillForm: c.pillForm,
    pillHalfSteps: c.pillHalfSteps,
    pillQuarterSteps: c.pillQuarterSteps,
    ingredients,
  };
}

function itemFromEvent(
  payload: Record<string, unknown>
): DoseControlItem & { name: string; strength: string } {
  const name = typeof payload.name === "string" ? payload.name : "Medication";
  const dose = typeof payload.dose === "number" ? payload.dose : undefined;
  const unit = typeof payload.unit === "string" ? (payload.unit as MedUnit) : undefined;
  const ingredients = Array.isArray(payload.ingredients)
    ? (payload.ingredients as MedIngredient[])
    : undefined;
  return {
    name,
    strength: formatMedStrength({ dose, unit, ingredients }),
    dose,
    unit,
    ingredients,
  };
}

// Units that count discrete dose forms — these get a +/- quantity stepper instead
// of a free numeric field (you take "2 tablets", not "2 mg of tablet").
const COUNTABLE_UNITS = new Set<MedUnit>(["tablet", "capsule", "spray", "puff", "drop", "patch"]);

type Step = "pick" | "adjust" | "review" | "done";

export function QuickMedLog({
  patientId,
  onClose,
  onLogged,
  onLoggedBatch,
}: {
  patientId: string;
  onClose: () => void;
  /** Called after a successful single-item save so the parent can toast + refresh. */
  onLogged: (ref: SavedEventRef, label: string) => void;
  /** Called after a successful multi-select batch save (no per-event undo toast — D-8
   *  in MEDICATION_BATCH_LOGGING_PLAN.md). */
  onLoggedBatch?: () => void;
}) {
  const { choices, regimenGroups, loading, error: loadError } = useMedChoices(patientId);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const [step, setStep] = useState<Step>("pick");
  const [selected, setSelected] = useState<MedChoice | null>(null);
  const [doseInput, setDoseInput] = useState(""); // numeric dose or quantity
  const [pillCountInput, setPillCountInput] = useState("");
  const [useExactAmount, setUseExactAmount] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [timeMode, setTimeMode] = useState<"now" | "custom">("now");
  const [customDate, setCustomDate] = useState(todayStr);
  const [customTime, setCustomTime] = useState(() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  });

  // Multi-select mode (MEDICATION_BATCH_LOGGING_PLAN.md D-1): off by default, an
  // explicit opt-in toggle on the pick step. Keys are `${regimenId}:${lineId}`.
  const [multiMode, setMultiMode] = useState(false);
  const [multiSelected, setMultiSelected] = useState<Set<string>>(new Set());
  // Which regimen group rows are expanded to show their member meds (keyed by
  // regimenId). Collapsed by default so the pick list stays compact.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Whether the current `review` step was reached via the batch path — determines
  // where "Back" goes and what the "done" step says.
  const [reviewedFromMulti, setReviewedFromMulti] = useState(false);

  function selectCustomMode() {
    const n = new Date();
    setCustomTime(`${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`);
    setCustomDate(todayStr());
    setTimeMode("custom");
  }

  const [pending, setPending] = useState<ProactiveSuggestion | null>(null);
  const [busy, setBusy] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Batch Review & adjust (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md). reviewItems
  // order matches pending.events (hazard 3). doseEdits keys are regimenId:lineId.
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [doseEdits, setDoseEdits] = useState<Record<string, number>>({});
  const [checkState, setCheckState] = useState<"ready" | "dirty" | "checking" | "error">("ready");

  const patientIdRef = useRef(patientId);
  patientIdRef.current = patientId;
  const runnerRef = useRef<ReturnType<
    typeof createLatestRunner<[{ regimenId: string; lineId: string; dose?: number }[]], ProactiveSuggestion>
  > | null>(null);
  if (runnerRef.current === null) {
    runnerRef.current = createLatestRunner({
      delayMs: 400,
      run: (items) => quickLogMedicationBatch(patientIdRef.current, items),
      onStart: () => setCheckState("checking"),
      onResult: (sugg) => {
        setPending(sugg);
        setCheckState("ready");
        setError(null);
      },
      onError: (err) => {
        setCheckState("error");
        setError(err instanceof ApiError ? err.message : "Couldn't re-check safety");
      },
    });
  }
  useEffect(() => {
    const runner = runnerRef.current;
    return () => runner?.cancel();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim();
    // Matches medication name OR its free-text "used for" field (comma-separated
    // uses, curated synonyms) — e.g. searching "reflux" also finds a med tagged
    // "heartburn" (MEDICATION_QUICK_LOG search-by-indication).
    return q ? choices.filter((c) => matchesMedicationSearch(q, c.name, c.intendedFor)) : choices;
  }, [choices, query]);

  const asNeeded = filtered.filter((c) => c.fromAsNeeded);
  const scheduled = filtered.filter((c) => !c.fromAsNeeded);

  // Saved scheduled regimens, matched on the group name OR any member med.
  const filteredGroups = useMemo(() => {
    const q = query.trim();
    if (!q) return regimenGroups;
    return regimenGroups.filter(
      (g) =>
        matchesMedicationSearch(q, g.name, undefined) ||
        g.items.some((i) => matchesMedicationSearch(q, i.name, i.intendedFor))
    );
  }, [regimenGroups, query]);

  function pickMed(c: MedChoice) {
    setSelected(c);
    setDoseInput(typeof c.dose === "number" ? String(c.dose) : "");
    if (hasPillStepper(c)) {
      setPillCountInput(String(pillCountForDose(c, c.dose ?? c.pillStrength ?? 0)));
      setUseExactAmount(false);
    } else {
      setPillCountInput("");
    }
    setNoteInput("");
    setShowNote(false);
    setError(null);
    setStep("adjust");
  }

  function toggleMultiMode() {
    setMultiMode((m) => !m);
    setMultiSelected(new Set());
    setDoseEdits({});
    setError(null);
  }

  function dropDoseEdit(key: string) {
    setDoseEdits((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function toggleMedInBatch(c: MedChoice) {
    const key = `${c.regimenId}:${c.lineId}`;
    if (multiSelected.has(key)) dropDoseEdit(key);
    setMultiSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleGroupExpanded(regimenId: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(regimenId)) next.delete(regimenId);
      else next.add(regimenId);
      return next;
    });
  }

  // Multi-select: checking a regimen adds every member key; if all members are
  // already in the set, the toggle clears them (all-or-nothing at the group level,
  // individual members still toggle on their own).
  function toggleRegimenInBatch(group: RegimenGroupChoice) {
    const keys = group.items.map((i) => `${i.regimenId}:${i.lineId}`);
    const allIn = keys.every((k) => multiSelected.has(k));
    if (allIn) {
      for (const k of keys) dropDoseEdit(k);
    }
    setMultiSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (allIn) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  }

  // Single-select: log the whole regimen now. Reuses the batch prepare endpoint
  // (each med at its saved default dose) and drops straight to the review step,
  // exactly like the multi-select batch path.
  async function pickRegimen(group: RegimenGroupChoice) {
    setBusy(true);
    setError(null);
    runnerRef.current?.cancel();
    try {
      const items = group.items.map((i) => ({ regimenId: i.regimenId, lineId: i.lineId }));
      const sugg = await quickLogMedicationBatch(patientId, withDoses(items, doseEdits));
      setReviewItems(items);
      setPending(sugg);
      setCheckState("ready");
      setReviewedFromMulti(true);
      setStep("review");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't prepare the regimen");
    } finally {
      setBusy(false);
    }
  }

  const isCountable = !!selected?.unit && COUNTABLE_UNITS.has(selected.unit);
  const usesPillStepper = !!selected && hasPillStepper(selected);

  // The dose override to send to the prepare call (undefined for combination products).
  function doseOverride(): number | undefined {
    if (!selected || selected.hasIngredients) return undefined;
    const v = parseFloat(doseInput);
    return Number.isFinite(v) && v > 0 ? v : undefined;
  }

  async function review() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    runnerRef.current?.cancel();
    try {
      const sugg = await quickLogMedication(patientId, selected.regimenId, selected.lineId, doseOverride(), noteInput);
      setPending(sugg);
      setReviewedFromMulti(false);
      setStep("review");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't prepare the log");
    } finally {
      setBusy(false);
    }
  }

  async function reviewBatch() {
    if (multiSelected.size === 0) return;
    setBusy(true);
    setError(null);
    runnerRef.current?.cancel();
    try {
      const items = [...multiSelected].map((k) => {
        const [regimenId, lineId] = k.split(":");
        return { regimenId: regimenId!, lineId: lineId! };
      });
      const sugg = await quickLogMedicationBatch(patientId, withDoses(items, doseEdits));
      setReviewItems(items);
      setPending(sugg);
      setCheckState("ready");
      setReviewedFromMulti(true);
      setStep("review");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't prepare the batch");
    } finally {
      setBusy(false);
    }
  }

  function editBatchDose(key: string, dose: number) {
    const next = { ...doseEdits, [key]: dose };
    setDoseEdits(next);
    setCheckState("dirty");
    runnerRef.current?.schedule(withDoses(reviewItems, next));
  }

  function retrySafetyCheck() {
    setError(null);
    setCheckState("dirty");
    runnerRef.current?.schedule(withDoses(reviewItems, doseEdits));
  }

  function backFromReview() {
    runnerRef.current?.cancel();
    setCheckState("ready");
    setError(null);
    setStep(reviewedFromMulti ? "pick" : "adjust");
  }

  async function save() {
    if (!pending) return;
    let customIso: string | undefined;
    if (timeMode === "custom" && customTime) {
      const [year, month, day] = customDate.split("-").map(Number);
      const [h, m] = customTime.split(":").map(Number);
      const d = new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0);
      customIso = d.toISOString();
    }
    setBusy(true);
    setError(null);
    try {
      const { saved } = await confirmEvents(patientId, pending.pendingId, undefined, timeMode, customIso);
      if (pending.events.length > 1) {
        onLoggedBatch?.();
        setStep("done");
      } else {
        const ref = saved[0];
        if (ref) onLogged(ref, `Logged ${selected?.name ?? "medication"}`);
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  function logAnother() {
    runnerRef.current?.cancel();
    setSelected(null);
    setPending(null);
    setDoseInput("");
    setNoteInput("");
    setShowNote(false);
    setMultiMode(false);
    setMultiSelected(new Set());
    setExpandedGroups(new Set());
    setReviewedFromMulti(false);
    setReviewItems([]);
    setDoseEdits({});
    setCheckState("ready");
    setTimeMode("now");
    setCustomDate(todayStr());
    setQuery("");
    setError(null);
    setStep("pick");
  }

  const recordTime =
    timeMode === "custom" && customTime
      ? (() => {
          const [year, month, day] = customDate.split("-").map(Number);
          const [h, m] = customTime.split(":").map(Number);
          const d = new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0);
          const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
          const isToday = customDate === todayStr();
          return isToday ? timeStr : `${timeStr} on ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
        })()
      : new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  function findChoice(regimenId: string, lineId: string): MedChoice | undefined {
    const key = `${regimenId}:${lineId}`;
    return (
      choices.find((c) => `${c.regimenId}:${c.lineId}` === key) ??
      regimenGroups.flatMap((g) => g.items).find((c) => `${c.regimenId}:${c.lineId}` === key)
    );
  }

  function doseForBatchIndex(i: number): {
    key: string;
    item: DoseControlItem & { name: string; strength: string };
    dose: number;
    flagged: boolean;
  } | null {
    const ev = pending?.events[i];
    if (!ev) return null;
    const ref = reviewItems[i];
    const choice = ref ? findChoice(ref.regimenId, ref.lineId) : undefined;
    const item = choice ? choiceToItem(choice) : itemFromEvent(ev.payload);
    const key = ref ? `${ref.regimenId}:${ref.lineId}` : "";
    const edited = key ? doseEdits[key] : undefined;
    const raw = edited !== undefined ? edited : eventDose(ev.payload);
    const dose = raw > 0 ? raw : usualDose(item);
    return { key, item, dose, flagged: !!choice?.overdose };
  }

  let adjustedCount = 0;
  if (pending && pending.events.length > 1) {
    for (let i = 0; i < pending.events.length; i++) {
      const row = doseForBatchIndex(i);
      if (row && isAdjusted(row.item, row.dose)) adjustedCount++;
    }
  }
  const batchReview = !!pending && pending.events.length > 1;
  const saveLocked = batchReview && checkState !== "ready";

  return (
    <>
    <ModalShell
      title={
        step === "review" && (pending?.events.length ?? 0) > 1 ? (
          "Review & adjust"
        ) : step === "pick" || !selected ? (
          "Log a medication"
        ) : (
          // Info button sits right next to the name rather than flush against
          // the header's close button, so a thumb reaching for it on a phone
          // doesn't risk mis-tapping "close" instead (feedback from live use).
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate">{selected.name}</span>
            <button
              onClick={() => setShowInfo(true)}
              className="shrink-0 rounded-full p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Medication info"
              title="Medication info"
            >
              <InfoIcon size={16} />
            </button>
          </span>
        )
      }
      icon={<PillIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      subheader={
        step === "pick" && !loading && choices.length > 0 ? (
          <ClearableInput
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or what it's for…"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
        ) : undefined
      }
      bodyClassName={"overflow-y-auto p-4" + (step === "pick" ? " h-[50vh]" : "")}
    >
      {(error || loadError) && (
            <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error || loadError}
            </p>
          )}

          {/* ── Step: pick ─────────────────────────────────────────────── */}
          {step === "pick" && (
            <>
              {loading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading medications…</p>
              ) : choices.length === 0 ? (
                <div className="space-y-3 py-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">No medications saved yet.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Add one from the Medications page, then log doses here.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-2 flex justify-end">
                    <button
                      onClick={toggleMultiMode}
                      className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {multiMode ? "Cancel" : "Select multiple"}
                    </button>
                  </div>
                  {filtered.length === 0 && filteredGroups.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No matches.</p>
                  )}
                  {filteredGroups.length > 0 && (
                    <div className="mb-3">
                      <p className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        Regimens
                      </p>
                      <ul className="space-y-1">
                        {filteredGroups.map((g) =>
                          multiMode ? (
                            <RegimenGroupRow
                              key={g.regimenId}
                              group={g}
                              expanded={expandedGroups.has(g.regimenId)}
                              onToggleExpand={() => toggleGroupExpanded(g.regimenId)}
                              selected={multiSelected}
                              onToggleGroup={toggleRegimenInBatch}
                              onToggleItem={toggleMedInBatch}
                            />
                          ) : (
                            <RegimenGroupRow
                              key={g.regimenId}
                              group={g}
                              expanded={expandedGroups.has(g.regimenId)}
                              onToggleExpand={() => toggleGroupExpanded(g.regimenId)}
                              onPick={pickRegimen}
                            />
                          )
                        )}
                      </ul>
                    </div>
                  )}
                  {asNeeded.length > 0 &&
                    (multiMode ? (
                      <MedSection title="As needed" meds={asNeeded} selected={multiSelected} onToggle={toggleMedInBatch} />
                    ) : (
                      <MedSection title="As needed" meds={asNeeded} onPick={pickMed} />
                    ))}
                  {scheduled.length > 0 &&
                    (multiMode ? (
                      <MedSection title="Scheduled" meds={scheduled} selected={multiSelected} onToggle={toggleMedInBatch} />
                    ) : (
                      <MedSection title="Scheduled" meds={scheduled} onPick={pickMed} />
                    ))}
                  {multiMode && multiSelected.size > 0 && (
                    <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3">
                      <button
                        onClick={() => void reviewBatch()}
                        disabled={busy}
                        className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
                      >
                        {busy ? "Checking…" : `Review & adjust (${multiSelected.size})`}
                      </button>
                      <p className="mt-1.5 text-center text-[11px] text-gray-500 dark:text-gray-400">
                        You can change amounts on the next screen
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Step: adjust ───────────────────────────────────────────── */}
          {step === "adjust" && selected && (
            <div className="space-y-4">
              {/* Dose / quantity */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {selected.hasIngredients ? "Dose" : isCountable ? "Quantity" : "Dose"}
                </label>
                {selected.hasIngredients ? (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{selected.strength}</p>
                ) : usesPillStepper && !useExactAmount ? (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          const next = roundPillCount(
                            selected,
                            (parseFloat(pillCountInput) || 1) - pillStep(selected)
                          );
                          setPillCountInput(String(next));
                          setDoseInput(String(doseForPillCount(selected, next)));
                        }}
                        className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-700 text-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        aria-label="Decrease pill count"
                      >
                        −
                      </button>
                      <span className="min-w-24 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {formatPillCount(selected, parseFloat(pillCountInput) || 1)}
                      </span>
                      <button
                        onClick={() => {
                          const next = roundPillCount(
                            selected,
                            (parseFloat(pillCountInput) || 1) + pillStep(selected)
                          );
                          setPillCountInput(String(next));
                          setDoseInput(String(doseForPillCount(selected, next)));
                        }}
                        className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-700 text-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        aria-label="Increase pill count"
                      >
                        +
                      </button>
                    </div>
                    <div className="space-y-0.5">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        = {doseInput || 0} {selected.unit}
                      </p>
                      <button
                        onClick={() => setUseExactAmount(true)}
                        className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Enter exact amount
                      </button>
                    </div>
                  </div>
                ) : isCountable ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        setDoseInput((d) => {
                          const step = pillStep(selected);
                          const cur = parseFloat(d) || step;
                          return String(Math.max(step, Math.round((cur - step) * 100) / 100));
                        })
                      }
                      className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-700 text-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span className="min-w-12 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {parseFloat(doseInput) || pillStep(selected)} {selected.unit}
                      {(parseFloat(doseInput) || pillStep(selected)) > 1 ? "s" : ""}
                    </span>
                    <button
                      onClick={() =>
                        setDoseInput((d) => {
                          const step = pillStep(selected);
                          const cur = parseFloat(d) || step;
                          return String(Math.round((cur + step) * 100) / 100);
                        })
                      }
                      className="h-9 w-9 rounded-full border border-gray-300 dark:border-gray-700 text-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      inputMode="decimal"
                      // For a med with no pill size configured, the browser's native
                      // up/down spinner arrows step (and floor) by the med's own
                      // recorded dose rather than 1 — "one more/fewer standard dose".
                      // Meds using the pill-count stepper's "enter exact amount"
                      // fallback keep unrestricted free entry (`useExactAmount` here
                      // implies `usesPillStepper`, so `selected` already has a
                      // pillStrength and this branch is purely the escape hatch).
                      step={!usesPillStepper && selected.dose && selected.dose > 0 ? selected.dose : "any"}
                      min={!usesPillStepper && selected.dose && selected.dose > 0 ? selected.dose : undefined}
                      value={doseInput}
                      onChange={(e) => setDoseInput(e.target.value)}
                      className="w-28 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">{selected.unit ?? ""}</span>
                  </div>
                )}
                {usesPillStepper && useExactAmount && (
                  <button
                    onClick={() => {
                      // Switching back to pill-count mode: resync from whatever exact
                      // amount was typed, rounding to the nearest available step.
                      setPillCountInput(String(pillCountForDose(selected, parseFloat(doseInput) || 0)));
                      setUseExactAmount(false);
                    }}
                    className="mt-2 text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Use pill count instead
                  </button>
                )}
              </div>

              {/* Time */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Time</label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex rounded-full bg-gray-100 dark:bg-gray-800 p-0.5 text-xs font-medium">
                    <button
                      onClick={() => setTimeMode("now")}
                      className={
                        "rounded-full px-4 py-1 transition-all " +
                        (timeMode === "now"
                          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400")
                      }
                    >
                      Now
                    </button>
                    <button
                      onClick={selectCustomMode}
                      className={
                        "rounded-full px-4 py-1 transition-all " +
                        (timeMode === "custom"
                          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400")
                      }
                    >
                      Custom
                    </button>
                  </div>
                  {timeMode === "custom" && (
                    <>
                      <input
                        type="date"
                        value={customDate}
                        min={minCustomDateStr()}
                        max={todayStr()}
                        onChange={(e) => setCustomDate(e.target.value)}
                        className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="time"
                        value={customTime}
                        max={customDate === todayStr() ? nowTimeStr() : undefined}
                        onChange={(e) => setCustomTime(e.target.value)}
                        className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Note (optional) */}
              <div>
                {showNote ? (
                  <>
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                      Note
                    </label>
                    <ClearableTextarea
                      autoFocus
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      placeholder="Add a note (optional)…"
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    />
                  </>
                ) : (
                  <button
                    onClick={() => setShowNote(true)}
                    className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    + Add a note
                  </button>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => void review()}
                  disabled={busy}
                  className="flex-1 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
                >
                  {busy ? "Checking…" : "Review"}
                </button>
                <button
                  onClick={() => setStep("pick")}
                  disabled={busy}
                  className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* ── Step: review ───────────────────────────────────────────── */}
          {step === "review" && pending && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Records at <span className="font-semibold text-gray-700 dark:text-gray-200">{recordTime}</span>
              </p>
              {pending.events.length > 1 ? (
                // Review & adjust (MEDICATION_BATCH_DOSE_ADJUST_PLAN.md). No per-row
                // ✕-remove — going "Back" to uncheck is the correction path.
                <div className="space-y-2">
                  {pending.events.map((_, i) => {
                    const row = doseForBatchIndex(i);
                    if (!row) return null;
                    return (
                      <DoseAdjustRow
                        key={row.key || i}
                        item={row.item}
                        dose={row.dose}
                        density="full"
                        flagged={row.flagged}
                        disabled={busy}
                        onChange={(dose) => {
                          if (row.key) editBatchDose(row.key, dose);
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                  {pending.events[0] ? medLabelWithoutTime(pending.events[0].confirmation) : null}
                </div>
              )}

              {batchReview ? (
                (checkState !== "ready" || pending.warningPrefix?.trim()) && (
                  <div
                    className={
                      "rounded-lg border px-3 py-2 text-xs " +
                      (checkState === "error"
                        ? "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
                        : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200")
                    }
                  >
                    {checkState === "error" ? (
                      <>
                        <p className="font-semibold">Couldn&apos;t re-check safety</p>
                        <p className="mt-1">
                          Your changes are kept.{" "}
                          <button
                            type="button"
                            onClick={retrySafetyCheck}
                            className="underline decoration-dotted underline-offset-2"
                          >
                            Retry
                          </button>
                        </p>
                      </>
                    ) : checkState !== "ready" ? (
                      <>
                        <p className="flex items-center gap-2 font-semibold">
                          <DoseCheckSpinner className="h-3.5 w-3.5" />
                          Re-checking safety for your changes…
                        </p>
                        {pending.warningPrefix?.trim() && (
                          <p className="mt-1 whitespace-pre-wrap opacity-35">{pending.warningPrefix.trim()}</p>
                        )}
                      </>
                    ) : (
                      <p className="whitespace-pre-wrap">{pending.warningPrefix!.trim()}</p>
                    )}
                  </div>
                )
              ) : (
                pending.warningPrefix?.trim() && (
                  <div className="whitespace-pre-wrap rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                    {pending.warningPrefix.trim()}
                  </div>
                )
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => void save()}
                  disabled={busy || saveLocked}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
                >
                  {busy ? (
                    "Saving…"
                  ) : batchReview && checkState === "error" ? (
                    "Retry check to save"
                  ) : batchReview && checkState !== "ready" ? (
                    <>
                      <DoseCheckSpinner className="h-3.5 w-3.5" onAccent />
                      Checking safety…
                    </>
                  ) : batchReview ? (
                    `Save ${pending.events.length} medications${adjustedCount > 0 ? ` (${adjustedCount} adjusted)` : ""}`
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  onClick={backFromReview}
                  disabled={busy}
                  className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          {/* ── Step: done ─────────────────────────────────────────────── */}
          {step === "done" && (
            <div className="space-y-4 py-4 text-center">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {pending && pending.events.length > 1
                  ? `✓ Logged ${pending.events.length} medications${adjustedCount > 0 ? ` (${adjustedCount} adjusted)` : ""}.`
                  : `✓ Logged ${selected?.name}.`}
              </p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={logAnother}
                  className="rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
                >
                  Log another
                </button>
                <button
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  Done
                </button>
              </div>
            </div>
          )}
    </ModalShell>
    {showInfo && selected && (
      <MedicationInfoModal drugName={selected.name} patientId={patientId} onClose={() => setShowInfo(false)} />
    )}
    </>
  );
}
